import { useState } from "react";
import { Building2, Megaphone, Phone, Mail, MessageCircle, MapPin } from "lucide-react";
import { Seo } from "@/components/common/Seo";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { SectionLabel } from "@/components/common/SectionLabel";
import { LeadForm } from "@/components/common/LeadForm";
import { SEO, BRAND } from "@/data/site";

const TABS = [
  { key: "advertiser", label: "Advertise with AdX", icon: Megaphone, title: "Start your first campaign", note: "Self-serve for local business, managed for brands & agencies.", cta: "Talk to sales" },
  { key: "society", label: "Partner Your Society", icon: Building2, title: "Get a proposal for your community", note: "Transparent terms, zero upfront cost.", cta: "Request a proposal" },
  { key: "contact", label: "Talk to Us", icon: MessageCircle, title: "Say hello", note: "Any other question? Drop us a message.", cta: "Send message" },
];

export default function Contact() {
  const [tab, setTab] = useState("advertiser");
  const active = TABS.find((t) => t.key === tab);

  return (
    <>
      <Seo title={SEO.contact.title} description={SEO.contact.description} />
      <PageHero
        label="Get Started"
        titleLines={["Let's Put Your", "Screens to Work."]}
        accentIndex={1}
        subtitle="Whether you're a society or an advertiser, you're two minutes from getting started."
      />

      <section className="bg-paper py-24 md:py-32">
        <div className="container-px grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <Reveal><SectionLabel>Choose Your Track</SectionLabel></Reveal>
            <div className="mt-8 flex flex-col gap-3">
              {TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTab(t.key)}
                  data-testid={`contact-tab-${t.key}`}
                  className={`group flex items-center gap-4 rounded-xl border p-5 text-left transition-colors duration-200 ${
                    tab === t.key ? "bg-ink text-white border-ink" : "bg-white border-ink/10 hover:border-ink/40"
                  }`}
                >
                  <span className={`grid place-items-center h-11 w-11 rounded-lg ${tab === t.key ? "bg-teal text-ink" : "bg-paper text-ink"}`}>
                    <t.icon className="h-5 w-5" strokeWidth={1.8} />
                  </span>
                  <span className="font-display font-700 text-lg">{t.label}</span>
                </button>
              ))}
            </div>

            <div className="mt-10 space-y-4 border-t border-ink/10 pt-8">
              <a href={`mailto:${BRAND.email}`} onClick={() => window.dataLayer?.push({ event: "contact_click" })} className="flex items-center gap-3 text-ink/70 hover:text-ink transition-colors">
                <Mail className="h-5 w-5 text-teal" /> {BRAND.email}
              </a>
              <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} onClick={() => window.dataLayer?.push({ event: "contact_click" })} className="flex items-center gap-3 text-ink/70 hover:text-ink transition-colors"><Phone className="h-5 w-5 text-teal" /> {BRAND.phone}</a>
              <div className="space-y-4 pt-2">
                {BRAND.offices.map((o) => (
                  <div key={o.city} className="flex items-start gap-3 text-ink/70">
                    <MapPin className="h-5 w-5 text-teal shrink-0 mt-0.5" />
                    <span><span className="font-display font-700 text-ink">{o.city}</span><br />{o.address}</span>
                  </div>
                ))}
              </div>
              <p className="pt-2 font-mono text-xs uppercase tracking-[0.16em] text-teal">We typically respond within 48 hours</p>
            </div>
          </div>

          <div className="lg:col-span-8">
            <LeadForm
              key={tab}
              variant={tab}
              title={active.title}
              note={active.note}
              ctaLabel={active.cta}
              testidPrefix={`contact-${tab}`}
            />
          </div>
        </div>
      </section>
    </>
  );
}
